import {
  Checkbox,
  Chip,
  FormControl,
  FormControlProps,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import Iconify from '../iconify';

type RHFChipSelectProps = FormControlProps & {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  multiple?: boolean;
  selectedItems?: { label: string; value: string }[];
  handleRemove?: (value: string) => void;
  options: {
    label: string;
    value: string;
  }[];
};

export default function RHFChipSelect({
  name,
  chip,
  label,
  options,
  checkbox,
  placeholder,
  helperText,
  multiple = true,
  selectedItems,
  handleRemove,
  ...other
}: RHFChipSelectProps) {
  const { control } = useFormContext();
  const renderValues = (selectedIds: string[] | string) => {
    let ids: string[] = [];
    if (Array.isArray(selectedIds)) {
      ids = selectedIds;
    } else if (selectedIds) {
      ids = [selectedIds];
    }

    if (!ids.length && placeholder) {
      return (
        <Typography typography="body-md" color="greyScale.300">
          {placeholder}
        </Typography>
      );
    }

    if (!multiple && ids.length > 0) {
      const selectedOption = options.find((opt) => opt.value === ids[0]);
      return <Typography typography="body-md">{selectedOption?.label || ids[0]}</Typography>;
    }

    return (
      <Typography typography="body-md" color="greyScale.300">
        {placeholder || 'Selecione'}
      </Typography>
    );
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const normalizeValue = (value: any): string[] | string => {
          if (!multiple) {
            // Para seleção única, retorna string
            if (Array.isArray(value)) return value[0] || '';
            return value || '';
          }
          // Para seleção múltipla, retorna array
          if (Array.isArray(value)) return value;
          if (value) return [value];
          return [];
        };
        const fieldValue = normalizeValue(field.value);

        return (
          <Grid container direction="row" alignItems="center" spacing={1} wrap="nowrap">
            <Grid container item md={4}>
              <FormControl fullWidth error={!!error} {...other}>
                {label && <InputLabel id={name}> {label} </InputLabel>}
                <Select
                  {...field}
                  value={fieldValue}
                  multiple={multiple}
                  displayEmpty={!!placeholder}
                  id={multiple ? `multiple-${name}` : name}
                  labelId={name}
                  label={label}
                  renderValue={renderValues}
                  placeholder={placeholder}
                  sx={{ minWidth: 200 }}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (multiple) {
                      field.onChange(value as string[]);
                    } else {
                      field.onChange(value as string);
                    }
                  }}
                >
                  {options.map((option) => {
                    const selected = multiple ? (fieldValue as string[]).includes(option.value) : fieldValue === option.value;
                    return (
                      <MenuItem key={option.value} value={option.value}>
                        {checkbox && <Checkbox size="small" disableRipple checked={selected} />}
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </Select>

                {(!!error || helperText) && <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item container spacing={1}>
              {multiple &&
                Array.isArray(fieldValue) &&
                fieldValue.map((value: string, index: number) => {
                  const item = options.find((opt) => opt.value === value);
                  if (!item) return null;
                  return (
                    <Grid item key={value}>
                      <Chip
                        sx={{ borderRadius: 100 }}
                        color="primary"
                        variant="filled"
                        clickable
                        label={`${index + 1}- ${item.label}`}
                        onDelete={() => {
                          const newValue = (fieldValue as string[]).filter((v: string) => v !== value);
                          field.onChange(newValue);
                        }}
                        deleteIcon={<Iconify icon="ri:close-line" height={16} width={16} />}
                      />
                    </Grid>
                  );
                })}
              {!multiple && typeof fieldValue === 'string' && fieldValue && (
                <Grid item>
                  <Chip
                    sx={{ borderRadius: 100 }}
                    color="primary"
                    variant="filled"
                    clickable
                    label={options.find((opt) => opt.value === fieldValue)?.label || fieldValue}
                    onDelete={() => {
                      field.onChange('');
                    }}
                    deleteIcon={<Iconify icon="ri:close-line" height={16} width={16} />}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        );
      }}
    />
  );
}
